import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import { UserContext } from './UserProvider';
import { userAxios, setupAxiosInterceptors } from '../userAxios';

const API_URL = process.env.REACT_APP_API_URL;

export const WebsiteFileContext = createContext({});

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, options);
};

const formatFileSize = (size) => {
  if (size < 1024) {
    return size + ' bytes';
  } else if (size < 1024 * 1024) {
    return (size / 1024).toFixed(2) + ' KB';
  } else if (size < 1024 * 1024 * 1024) {
    return (size / (1024 * 1024)).toFixed(2) + ' MB';
  } else if (size < 1024 * 1024 * 1024 * 1024) {
    return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  } else {
    return (size / (1024 * 1024 * 1024 * 1024)).toFixed(2) + ' TB';
  }
};

export const WebsiteFileProvider = (props) => {
  const { token, handleInvalidToken } = useContext(UserContext);
  const [websiteFiles, setWebsiteFiles] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const [showEmpty, setShowEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [tableData, setTableData] = useState([]);
  const [sourceData, setSourceData] = useState([]);
  const [websiteId, setWebsiteId] = useState('');

  useEffect(() => {
    setupAxiosInterceptors(handleInvalidToken);
  }, [handleInvalidToken]);

  const getDocuSignStatus = async (id) => {
    try {
      const response = await userAxios.get(
        `${API_URL}/document/${id}/signatures/status`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching DocuSign status:', error);
      return null;
    }
  };

  async function getWebsiteFiles(id) {
    try {
      const response = await userAxios.get(
        `${API_URL}/websites/${id}/documents`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setWebsiteFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  }

  const getFileTypes = useCallback(async () => {
    try {
      const response = await userAxios.get(`${API_URL}/documenttypes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFileTypes(response.data);
    } catch (error) {
      console.error('Error fetching file types:', error);
    }
  }, [token]);

  const apiRequest = async (method, url, data = null) => {
    try {
      const config = {
        method: method,
        url: `${API_URL}${url}`,
        headers: { Authorization: `Bearer ${token}` },
        ...(data && { data: data }),
      };
      const response = await userAxios(config);
      return response.data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  };

  const deleteFile = async (id) => {
    try {
      const data = await apiRequest('delete', `/document/${id}`);
      setWebsiteFiles(
        websiteFiles.filter((websiteFile) => websiteFile.rowKey !== id)
      );
      setShowEmpty(websiteFiles.length === 0);
      return data;
    } catch (err) {
      console.error('Error deleting file:', err);
    }
  };

  const handleFileUpload = async (formData, websiteId) => {
    if (!formData) return;
    setLoading(true);
    try {
      await userAxios.post(
        `${API_URL}/websites/${websiteId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLoading(false);
      await getWebsiteFiles(websiteId);
      request();
    } catch (error) {
      setLoading(false);
      console.error('Error uploading file:', error);
    }
  };

  const getDocuSignDetails = async (id, websiteId) => {
    try {
      const response = await userAxios.get(
        `${API_URL}/documenttype/${id}/sign?website=${websiteId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching DocuSign details:', error);
      throw error;
    }
  };

  const sendDocusign = async (id, payload) => {
    try {
      const response = await userAxios.post(
        `${API_URL}/documenttype/${id}/sign`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending DocuSign:', error);
      throw error;
    }
  };

  const downloadBlob = async (url, token, fileName) => {
    try {
      const response = await userAxios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      alert(`Couldn't fetch file ${fileName}`);
    }
  };

  const downloadFile = async (row) => {
    const url = `${API_URL}/document/${row.rowKey}`;
    const fileName = row.label;
    await downloadBlob(url, token, fileName);
  };

  const approveDocument = async (id) => {
    try {
      const response = await apiRequest('put', `/document/${id}/approve`);
      const updatedFiles = websiteFiles.map((websiteFile) =>
        websiteFile.rowKey === id
          ? { ...websiteFile, approved: true }
          : websiteFile
      );
      setWebsiteFiles(updatedFiles);
      return response;
    } catch (err) {
      console.error('Error approving document:', err);
      throw err;
    }
  };

  const resendDocument = async (id) => {
    try {
      const response = await apiRequest(
        'put',
        `/document/${id}/signatures/resend`
      );
      return response;
    } catch (err) {
      console.error('Error resending document:', err);
      throw err;
    }
  };

  const voidDocument = async (id, reason) => {
    try {
      const response = await apiRequest(
        'put',
        `/document/${id}/signatures/void`,
        { reason }
      );
      return response;
    } catch (err) {
      console.error('Error voiding document:', err);
      throw err;
    }
  };

  const request = useCallback(() => {
    setTimeout(() => {
      setShowEmpty(tableData.length === 0);
    }, 1500);
  }, [tableData]);

  useEffect(() => {
    request();
  }, [request, websiteFiles]);

  useEffect(() => {
    setTableData(sourceData);
  }, [sourceData]);

  return (
    <WebsiteFileContext.Provider
      value={{
        websiteFiles,
        setWebsiteFiles,
        fileTypes,
        showEmpty,
        loading,
        isVisible,
        searchValue,
        setSearchValue,
        tableData,
        setTableData,
        sourceData,
        setSourceData,
        websiteId,
        setWebsiteId,
        getFileTypes,
        getWebsiteFiles,
        deleteFile,
        handleFileUpload,
        getDocuSignStatus,
        downloadFile,
        approveDocument,
        request,
        formatDate,
        formatFileSize,
        resendDocument,
        voidDocument,
        sendDocusign,
        getDocuSignDetails,
      }}
    >
      {props.children}
    </WebsiteFileContext.Provider>
  );
};

export default WebsiteFileProvider;
